import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { AdminAccessDenied } from './AdminAccessDenied';
import { AdminErrorAlert, AdminEmptyState, AdminLoadingState, AdminTableWrap } from './admin-ui';

interface WaitingListEntry {
  id: string;
  email: string;
  writingTypes: string[];
  writingTools: string[];
  collaborationType: string | null;
  additionalInfo: string | null;
  referral: string | null;
  referralDetail: string | null;
  invitationCode: string | null;
  createdAt: string;
}

export const WaitingListAdmin: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [entries, setEntries] = useState<WaitingListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitingId, setInvitingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchEntries = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/admin/waiting-list?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/admin/waiting-list`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch waiting list entries');
        }

        const data = await response.json();
        setEntries(data.entries || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching waiting list entries:', err);
        setError(t('failed_to_load_waiting_list'));
      } finally {
        setLoading(false);
      }
    };

    fetchEntries();
  }, [token, user, masterKey, t]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleInviteUser = async (entryId: string) => {
    try {
      setInvitingId(entryId);
      setError(null);
      const url = masterKey
        ? `${API_BASE}/api/admin/waiting-list/${entryId}/invite?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/admin/waiting-list/${entryId}/invite`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to invite user');
      }

      const data = await response.json();
      setEntries(entries.map(entry => entry.id === entryId ? data.entry : entry));
    } catch (err) {
      console.error('Error inviting user:', err);
      setError(err instanceof Error ? err.message : t('failed_to_invite_user'));
    } finally {
      setInvitingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {error && <AdminErrorAlert>{error}</AdminErrorAlert>}

      {loading ? (
        <AdminLoadingState />
      ) : entries.length === 0 ? (
        <AdminEmptyState>{t('no_waiting_list_entries')}</AdminEmptyState>
      ) : (
        <AdminTableWrap>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('email')}</TableHead>
                <TableHead>{t('waiting_list_what_do_you_write')}</TableHead>
                <TableHead>{t('waiting_list_writing_tools')}</TableHead>
                <TableHead>{t('waiting_list_collaboration')}</TableHead>
                <TableHead>{t('waiting_list_additional_info')}</TableHead>
                <TableHead>{t('waiting_list_referral')}</TableHead>
                <TableHead>{t('waiting_list_referral_detail')}</TableHead>
                <TableHead>{t('invitation_code')}</TableHead>
                <TableHead>{t('created')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.email}</TableCell>
                  <TableCell>{entry.writingTypes.length > 0 ? entry.writingTypes.join(', ') : '-'}</TableCell>
                  <TableCell>{entry.writingTools.length > 0 ? entry.writingTools.join(', ') : '-'}</TableCell>
                  <TableCell>{entry.collaborationType || '-'}</TableCell>
                  <TableCell className="max-w-[300px] break-words">{entry.additionalInfo || '-'}</TableCell>
                  <TableCell>{entry.referral || '-'}</TableCell>
                  <TableCell className="max-w-[200px] break-words">{entry.referralDetail || '-'}</TableCell>
                  <TableCell>
                    {entry.invitationCode ? (
                      entry.invitationCode
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleInviteUser(entry.id)}
                        disabled={invitingId === entry.id}
                      >
                        {invitingId === entry.id ? t('inviting') : t('invite_user')}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(entry.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTableWrap>
      )}
    </div>
  );
};

